/**
 * KPI Type Definitions
 */

export interface KpiPeriod {
  start: string; // YYYY-MM-DD
  end: string;   // YYYY-MM-DD
}

export interface KpiData {
  seller_id: string;
  period: KpiPeriod;
  care_stays_started: number;
  active_care_stays: number;
  care_days: number;
  monthly_revenue_earned: number;
  lead_to_contract_cvr: number;
  paused_customers: number;
  lead_to_eb_cvr: number;
  eb_to_posting_cvr: number;
  posting_to_contract_cvr: number;
  termination_rate: number;
  active_customers?: number;
  calculated_at?: string;
  company_averages?: {
    lead_to_contract_cvr?: number;
    lead_to_eb_cvr?: number;
    eb_to_posting_cvr?: number;
    posting_to_contract_cvr?: number;
    termination_rate?: number;
  };
}

export interface KpiError {
  error: string;
  message: string;
  seller_id?: string;
  period?: KpiPeriod;
}

export interface KpiOverviewCardProps {
  sellerId: string;
  periodStart: string;
  periodEnd: string;
  className?: string;
}

export interface UseKpiDataReturn {
  data: KpiData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// KPI display configuration
export const KPI_CONFIG = {
  active_customers: {
    label: 'Aktive Kunden',
    icon: '👤',
    format: (value: number) => value?.toString() || '0',
    unit: '',
    tooltip: 'Kunden mit aktuell laufenden Betreuungen.'
  },
  care_stays_started: {
    label: 'Begonnene Pflegeaufenthalte',
    icon: '🏁',
    format: (value: number) => value?.toString() || '0',
    unit: '',
    tooltip: 'Anzahl der Pflegeaufenthalte, die in diesem Monat begonnen haben.'
  },
  active_care_stays: {
    label: 'Aktive Pflegeaufenthalte',
    icon: '📊',
    format: (value: number) => value?.toString() || '0',
    unit: '',
    tooltip: 'Dies ist die Anzahl der Pflegeaufenthalte, die mindestens einen Tag in diesem Monat stattgefunden haben. Achtung! Das ist nicht die Anzahl der Kunden. Ein Kunde kann auch zwei verschiedene Pflegeeinsätze in einem Monat haben.'
  },
  care_days: {
    label: 'Pflegetage',
    icon: '📅',
    format: (value: number) => value?.toLocaleString('de-DE') || '0',
    unit: '',
    tooltip: 'Dies ist die Gesamtzahl der Tage aller Kunden, die in diesem Monat gepflegt wurde.'
  },
  monthly_revenue_earned: {
    label: 'Umsatz',
    icon: '💰',
    format: (value: number) => value != null ? `€${value.toLocaleString('de-DE', { 
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    })}` : '€0',
    unit: '',
    tooltip: 'Hier wird der gesamte Umsatz des ausgewählten Monats angezeigt.'
  },
  lead_to_contract_cvr: {
    label: 'Lead → Vertrag',
    icon: '📈',
    format: (value: number) => value != null ? `${value.toFixed(1)}%` : '0.0%',
    unit: '',
    tooltip: 'Dies ist die wichtigste und übergeordnete Abschlussquote. Sie gibt Aufschluss darüber, wie viel Prozent aller Leads einen Vertrag abgeschlossen haben. Wenn der Lead erst in einem der späteren Monate einen Vertrag abgeschlossen hat, zählt dieser Abschluss trotzdem in die Konversionsrate des Monats, wo der Lead erworben wurde.'
  },
  paused_customers: {
    label: 'Pausierte Kunden',
    icon: '⏸️',
    format: (value: number) => value?.toString() || '0',
    unit: '',
    tooltip: 'Wenn ein Kunde zwar gerade eben keinen aktiven Pflegeeinsatz hat, der Vertrag aber auch nicht gekündigt ist.'
  },
  lead_to_eb_cvr: {
    label: 'Lead → Erfassungsbogen',
    icon: '👥',
    format: (value: number) => value != null ? `${value.toFixed(1)}%` : '0.0%',
    unit: '',
    tooltip: 'Gibt an, wie viel Prozent aller Leads einen Erfassungsbogen ausgefüllt haben. Wenn der Lead erst in einem der späteren Monate einen Erfassungsbogen ausgefüllt hat, zählt diese Konversion trotzdem in die Rate des Monats, wo der Lead erworben wurde.'
  },
  eb_to_posting_cvr: {
    label: 'Erfassungsbogen → Posting',
    icon: '📝',
    format: (value: number) => value != null ? `${value.toFixed(1)}%` : '0.0%',
    unit: '',
    tooltip: 'Zeigt an, wie viel Prozent der Erfassungsbögen zu einem Stellengesuch (Posting) geführt haben. Wenn das Posting erst in einem späteren Monat erstellt wurde, zählt es trotzdem in die Rate des Monats, in dem der Erfassungsbogen erstellt wurde.'
  },
  posting_to_contract_cvr: {
    label: 'Posting → Vertrag',
    icon: '✅',
    format: (value: number) => value != null ? `${value.toFixed(1)}%` : '0.0%',
    unit: '',
    tooltip: 'Zeigt an, wie viel Prozent der Stellengesuche (Postings) zu einem Vertrag geführt haben. Wenn der Vertrag erst in einem späteren Monat abgeschlossen wurde, zählt er trotzdem in die Rate des Monats, in dem das Posting erstellt wurde.'
  },
  termination_rate: {
    label: 'Kündigungsquote',
    icon: '❌',
    format: (value: number) => value != null ? `${value.toFixed(2)}%` : '0.00%',
    unit: '',
    tooltip: 'Anteil des Gesamtkundenstammes, der diesen Monat gekündigt hat.'
  }
} as const;